// Copyright (c) 2020 WiseTime. All rights reserved.

import { flatten, uniq } from "ramda"
import { i18n } from "@lingui/core"
import { t } from "@lingui/macro"
import {
  AddToTimeGroupRequestSchema,
  TimeGroup,
  TimeRow,
  TrashStatus,
} from "../../generated/wisetime/user/timeline/v4/timeline_pb"
import { sortTagsListAlphabetically } from "./sorting"
import { Init } from "../../app/rpc"

export const getMatchedTimeRowsForGroup = (
  timeRows: TimeRow[],
  timeGroup: TimeGroup,
  currentSiteId: string | undefined,
): TimeRow[] => {
  const groupTagNames = timeGroup.tags
    .filter(t => t.siteId === currentSiteId)
    .map(groupTag => groupTag.name)

  if (groupTagNames.length === 0) {
    return []
  }

  return timeRows
    .filter(row => row.trashStatus !== TrashStatus.TRASHED
      && (row.timeGroupId === undefined || row.timeGroupId === "")
      && row.tags.filter(t => t.siteId === currentSiteId).some(rowTag => groupTagNames.includes(rowTag.name))
    )
}

export const extractUniqueTagIdsFromTimeRows = (
  timeRows: TimeRow[],
  currentSiteId: string | undefined,
): string[] => {
  return uniq(flatten(
    timeRows.map(timeRow => timeRow.tags
      .filter(t => t.siteId === currentSiteId)
      .map(t => t.id))
  ))
}

export const extractUniqueTagCategoryIdsFromTimeRows = (
  timeRows: TimeRow[],
  currentSiteId: string | undefined,
): string[] => {
  return uniq(flatten(
    timeRows.map(timeRow => timeRow.tagCategories
      .filter(t => t.siteId === currentSiteId)
      .map(t => t.id))
  ))
}

export const getMatchedTagTimeRows = (
  selectedTimeRows: TimeRow[],
  timeRowsForDate: TimeRow[],
  currentSiteId: string | undefined,
): TimeRow[] => {
  const tagIdsFromSelectedRows = extractUniqueTagIdsFromTimeRows(selectedTimeRows, currentSiteId)
  const tagCategoryIdsFromSelectedRows = extractUniqueTagCategoryIdsFromTimeRows(selectedTimeRows, currentSiteId)
  if (tagIdsFromSelectedRows.length === 0 && tagCategoryIdsFromSelectedRows.length === 0) {
    return []
  }

  const hasSameTagOrTagCategory = (timeRow: TimeRow) =>
    timeRow.tags.filter(t => t.siteId === currentSiteId)
      .some(({ id }) => tagIdsFromSelectedRows.includes(id)) ||
    timeRow.tagCategories.filter(t => t.siteId === currentSiteId)
      .some(({ id }) => tagCategoryIdsFromSelectedRows.includes(id))

  const selectedTimeRowIds = selectedTimeRows.map(r => r.id)
  return timeRowsForDate.filter(row => !selectedTimeRowIds.includes(row.id)
        && row.trashStatus !== TrashStatus.TRASHED
        && row.timeGroupId === ""
        && hasSameTagOrTagCategory(row))
}

export const hasTimeGroup = (row: TimeRow): boolean => Boolean(row.timeGroupId)

export const hasTrashedItems = (timeRows: TimeRow[]): boolean =>
  timeRows.some(timeRow => timeRow.trashStatus === TrashStatus.TRASHED)

export const countUnpostedUntrashedUngroupedRows = (timeRows: TimeRow[]): number =>
  timeRows.filter(timeRow => timeRow.trashStatus !== TrashStatus.TRASHED && timeRow.timeGroupId === "").length

export const getDescription = (description: string): string => {
  if (description === "@_empty_@") {
    return "-- " + i18n._(t({ id: "msg_no_window_title_available", message: "No window title available" })) + " --"
  }
  if (description === "@_blank_@") {
    return "-- " + i18n._(t({ id: "msg_no_window_title_found", message: "No window title found" })) + " --"
  }
  return description
}

/**
 * Creates AddToTimeGroupRequest instances for adding matching time rows to multiple time groups.
 * 
 * This function implements batch processing for the magic wand functionality, allowing time rows
 * to be added to multiple time groups simultaneously based on tag matching.
 * 
 * @param timeGroups - Array of time groups to process for adding time rows
 * @param timeRows - Array of available time rows that can be added to groups
 * @param currentSiteId - Current site ID used for tag filtering (undefined means no matches possible)
 * 
 * @returns Array of AddToTimeGroupRequest instances, each containing a time group ID and list of matching time row IDs
 * 
 * Behavior:
 * - Only processes time rows that are not trashed, not already grouped, and have matching site tags
 * - Implements conflict resolution: if a time row matches multiple groups, it's assigned to the first matching group only
 * - Returns empty array if currentSiteId is undefined or no matches are found
 * - Each request contains all matching time rows for a specific time group
 */
export const buildAddToGroupRequests = (
  timeGroups: TimeGroup[],
  timeRows: TimeRow[],
  currentSiteId: string | undefined
): Init<typeof AddToTimeGroupRequestSchema>[] => {
  // If no currentSiteId, no tags will match
  if (currentSiteId === undefined) {
    return []
  }

  const requests: Init<typeof AddToTimeGroupRequestSchema>[] = []
  const alreadyAssignedRowIds = new Set<string>()

  timeGroups.forEach(timeGroup => {
    const allMatchedTimeRows = getMatchedTimeRowsForGroup(timeRows, timeGroup, currentSiteId)
    const availableMatchedRows = allMatchedTimeRows.filter(row => !alreadyAssignedRowIds.has(row.id))
    
    if (availableMatchedRows.length > 0) {
      availableMatchedRows.forEach(row => alreadyAssignedRowIds.add(row.id))
      
      requests.push({
        timeGroupId: timeGroup.id,
        timeRowIds: availableMatchedRows.map(r => r.id),
      })
    }
  })

  return requests
}

type TimeRowsByTags = Record<string, TimeRow[]>
export const groupTimeRowsByTags = (timeRows: TimeRow[], currentSiteId?: string) =>
  timeRows.reduce((result: TimeRowsByTags, timeRow: TimeRow) => {
    const key = timeRow.tags
      .filter(t => t.siteId === currentSiteId)
      .sort(sortTagsListAlphabetically)
      .map(t => t.name)
      .join("")
    if (result[key] === undefined) {
      result[key] = [timeRow]
    } else {
      result[key]?.push(timeRow)
    }
    return result
  }, {})
